import { useEffect, useRef } from 'react';
import { useKioskStore } from '@/store/kiosk-store';
import { ApiClient, AuthError } from '@/lib/api-client';
import type { KioskConfig, JWTPayload } from '@museum-kiosk/shared-types';

const REVALIDATE_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

const client = new ApiClient();

/**
 * Decodes the JWT payload without verification (client-side only).
 * Returns null if the token is malformed.
 */
function decodeJwtPayload(token: string): JWTPayload | null {
  try {
    const [, payloadB64] = token.split('.');
    if (!payloadB64) return null;
    const json = atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as JWTPayload;
  } catch {
    return null;
  }
}

/**
 * When a JWT is present in the store, fetches the kiosk config and
 * revalidates it every 5 minutes.
 */
export function useConfig(): void {
  const jwt = useKioskStore((s) => s.jwt);
  const setConfig = useKioskStore((s) => s.setConfig);
  const deprovision = useKioskStore((s) => s.deprovision);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!jwt) return;

    const payload = decodeJwtPayload(jwt);
    if (!payload) {
      deprovision();
      return;
    }

    const kioskId = payload.kiosk_id;
    let mounted = true;

    const fetchConfig = async () => {
      try {
        const config = await client.get<KioskConfig>(`/api/v1/kiosks/${kioskId}/config`);
        if (mounted) setConfig(config);
      } catch (err) {
        if (err instanceof AuthError) {
          deprovision();
          return;
        }
        // Other errors — keep last known config
      }
    };

    void fetchConfig();

    intervalRef.current = setInterval(() => {
      if (mounted) void fetchConfig();
    }, REVALIDATE_INTERVAL_MS);

    return () => {
      mounted = false;
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [jwt, setConfig, deprovision]);
}
